import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  SvgIcon,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Dashboard as DashboardIcon,
  CardGiftcard as PerksIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';

const LogoIcon = () => (
  <SvgIcon viewBox="0 0 128 128" sx={{ fontSize: 32 }}>
    <g fill="none" stroke="currentColor" strokeWidth="10" strokeLinecap="round">
      <line x1="64" y1="64" x2="64" y2="24"/>
      <line x1="64" y1="64" x2="104" y2="64"/>
      <line x1="64" y1="64" x2="64" y2="104"/>
      <line x1="64" y1="64" x2="24" y2="64"/>
      <circle cx="64" cy="64" r="52" opacity="0.18"/>
    </g>
    <g fill="currentColor">
      <circle cx="64" cy="64" r="9"/>
      <circle cx="64" cy="24" r="9"/>
      <circle cx="104" cy="64" r="9"/>
      <circle cx="64" cy="104" r="9"/>
      <circle cx="24" cy="64" r="9"/>
    </g>
  </SvgIcon>
);

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, signInWithGoogle, signOut, isVerified } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showPill, setShowPill] = useState(false);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop;
      setScrolled(y > 40);
      setShowPill((prev) => (prev ? true : y > 200));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, my: 2 }}>
        <LogoIcon />
        <Typography variant="h6">mdcv.me</Typography>
      </Box>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/search')}>
            <ListItemText primary="Search" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/search')}>
            <ListItemText primary="Find your Profile" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/merchants')}>
            <ListItemText primary="For Businesses" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/faq')}>
            <ListItemText primary="FAQ" />
          </ListItemButton>
        </ListItem>
        {user ? (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/dashboard')}>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            </ListItem>
            {isVerified && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => navigate('/perks')}>
                  <ListItemText primary="Perks" />
                </ListItemButton>
              </ListItem>
            )}
          </>
        ) : (
          <ListItem disablePadding>
            <ListItemButton onClick={signInWithGoogle}>
              <ListItemText primary="Sign In" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          pt: 'env(safe-area-inset-top)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, md: scrolled ? 56 : 64 } }}>
            {/* Left: Hamburger on mobile, logo on desktop */}
            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Box
                component={Link}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  textDecoration: 'none',
                  color: 'primary.main',
                  mr: 2,
                }}
              >
                <LogoIcon />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'inherit',
                  }}
                >
                  mdcv.me
                </Typography>
              </Box>
            )}

            {/* Centered brand on mobile */}
            {isMobile && (
              <Box
                component={Link}
                to="/"
                sx={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                  color: 'primary.main',
                }}
              >
                <LogoIcon />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'inherit',
                  }}
                >
                  mdcv.me
                </Typography>
              </Box>
            )}

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Right nav */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
                <Button startIcon={<SearchIcon />} onClick={() => navigate('/search')} color="inherit">
                  Search
                </Button>
                <Button onClick={() => navigate('/merchants')} color="inherit">
                  For Businesses
                </Button>
                {user && (
                  <>
                    <Button startIcon={<DashboardIcon />} onClick={() => navigate('/dashboard')} color="inherit">
                      Dashboard
                    </Button>
                    {isVerified && (
                      <Button startIcon={<PerksIcon />} onClick={() => navigate('/perks')} color="inherit">
                        Perks
                      </Button>
                    )}
                  </>
                )}
              </Box>
            )}

            {user ? (
              <div>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  <Avatar alt={user.displayName || user.email || ''} src={user.photoURL || ''} sx={{ width: 32, height: 32 }} />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  keepMounted
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <MenuItem onClick={() => { handleClose(); navigate('/dashboard'); }}>Dashboard</MenuItem>
                  <MenuItem onClick={() => { handleClose(); navigate('/settings'); }}>Settings</MenuItem>
                  <Divider />
                  <MenuItem onClick={() => { handleClose(); signOut(); }}>Sign Out</MenuItem>
                </Menu>
              </div>
            ) : (
              <Button
                variant={isMobile ? 'text' : 'contained'}
                startIcon={<PersonAddIcon />}
                onClick={signInWithGoogle}
              >
                Sign In
              </Button>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>

      <Box component="main" sx={{ minHeight: 'calc(100vh - 64px)', backgroundColor: 'background.default' }}>
        {children}
      </Box>

      <Footer />

      {/* Bottom mobile shortcut pill */}
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          display: { xs: 'flex', md: 'none' },
          justifyContent: 'center',
          pb: 'calc(env(safe-area-inset-bottom) + 12px)',
          pointerEvents: 'none',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/search')}
          sx={{
            pointerEvents: 'auto',
            borderRadius: 999,
            height: 48,
            px: 3,
            boxShadow: 3,
            opacity: showPill ? 1 : 0,
            transform: showPill ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 150ms ease, transform 150ms ease',
          }}
        >
          Find your profile
        </Button>
        {showPill && (
          <IconButton
            size="small"
            onClick={() => setShowPill(false)}
            sx={{ position: 'absolute', right: 16, bottom: 20, pointerEvents: 'auto', bgcolor: 'background.paper', boxShadow: 2 }}
          >
            ✕
          </IconButton>
        )}
      </Box>
    </>
  );
};

export default Layout;
